import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  CloudUploadOutlined as CloudUploadOutlinedIcon,
  RefreshOutlined as RefreshOutlinedIcon,
  SearchOutlined as SearchOutlinedIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { CLINIC_DOCS_LAYOUT } from '../constants/layout';
import {
  useGetAdminClinicDocumentFileQuery,
  useGetAdminClinicDocumentQuery,
  useGetAdminClinicDocumentSectionsQuery,
  useListAdminClinicDocumentsQuery,
  useUploadAdminClinicDocumentsMutation,
} from '../store';
import type { ClinicDocumentCardResponse } from '../types';

import { ClinicDocumentDropZone } from './ClinicDocumentDropZone';
import { ClinicDocumentSectionTreeItem } from './ClinicDocumentSectionTreeItem';
import { DocumentMarkdownRenderer } from './DocumentMarkdownRenderer';

const PAGE_SIZE = 20;

const EMPTY_DOCUMENTS: ClinicDocumentCardResponse[] = [];

const DOC_KIND_OPTIONS = [
  { value: 'all', label: 'All kinds' },
  { value: 'contract', label: 'Contract' },
  { value: 'fee_schedule', label: 'Fee schedule' },
  { value: 'policy', label: 'Policy' },
  { value: 'manual', label: 'Manual' },
  { value: 'form', label: 'Form' },
  { value: 'credentialing', label: 'Credentialing' },
  { value: 'other', label: 'Other' },
] as const;

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'uploaded', label: 'Uploaded' },
  { value: 'metadata_ready', label: 'Metadata ready' },
  { value: 'processing', label: 'Processing' },
  { value: 'processed', label: 'Processed' },
  { value: 'failed', label: 'Failed' },
] as const;

const SCOPE_OPTIONS = [
  { value: 'all', label: 'All scopes' },
  { value: 'organization', label: 'Organization' },
  { value: 'provider', label: 'Provider' },
  { value: 'patient', label: 'Patient' },
] as const;

const formatDateTime = (value: string): string =>
  new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getStatusChipProps = (
  status: string,
): { color: 'default' | 'primary' | 'success' | 'error' | 'warning'; label: string } => {
  switch (status) {
    case 'processed':
      return { color: 'success', label: 'Processed' };
    case 'failed':
      return { color: 'error', label: 'Failed' };
    case 'processing':
      return { color: 'warning', label: 'Processing' };
    case 'metadata_ready':
      return { color: 'primary', label: 'Metadata ready' };
    case 'uploaded':
      return { color: 'default', label: 'Uploaded' };
    default:
      return { color: 'default', label: status.replace(/_/g, ' ') };
  }
};

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'processed':
      return '#4caf50';
    case 'failed':
      return '#f44336';
    case 'processing':
      return '#ff9800';
    case 'metadata_ready':
      return '#1976d2';
    default:
      return '#bdbdbd';
  }
};

// ---------------------------------------------------------------------------
// Admin file viewer (uses admin endpoint)
// ---------------------------------------------------------------------------
function AdminFileViewer({
  open,
  onClose,
  organizationId,
  clinicDocumentId,
  filename,
  title,
  contentType,
}: {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  clinicDocumentId: string | null;
  filename: string;
  title: string | null;
  contentType: string;
}) {
  const { data: blob, isLoading } = useGetAdminClinicDocumentFileQuery(
    { organizationId, clinicDocumentId: clinicDocumentId ?? '', disposition: 'inline' },
    { skip: !open || !clinicDocumentId },
  );
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  const blobUrl = useMemo(() => {
    if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    if (!blob) return null;
    const url = URL.createObjectURL(blob);
    blobUrlRef.current = url;
    return url;
  }, [blob]);

  const isPdf = contentType === 'application/pdf';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{title || filename}</DialogTitle>
      <DialogContent sx={{ p: 0, height: '80vh' }}>
        {isLoading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
            <CircularProgress />
          </Stack>
        ) : blobUrl ? (
          isPdf ? (
            <iframe src={blobUrl} title={filename} style={{ width: '100%', height: '100%', border: 'none' }} />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <img src={blobUrl} alt={filename} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            </Box>
          )
        ) : (
          <Alert severity="error" sx={{ m: 2 }}>Failed to load file.</Alert>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Admin detail panel (read-only)
// ---------------------------------------------------------------------------
function AdminDetailPanel({
  document,
  organizationId,
  dropZone,
}: {
  document: ClinicDocumentCardResponse | null;
  organizationId: string;
  dropZone: React.ReactNode;
}) {
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const clinicDocumentId = document?.clinic_document_id ?? null;

  const { data: detailData, isLoading: isDetailLoading } = useGetAdminClinicDocumentQuery(
    { organizationId, clinicDocumentId: clinicDocumentId ?? '' },
    { skip: !clinicDocumentId },
  );

  const effectiveDocument = detailData ?? document;
  const effectiveStatus = effectiveDocument?.processing_status ?? 'uploaded';

  const { data: sectionsData, isLoading: isSectionsLoading } = useGetAdminClinicDocumentSectionsQuery(
    { organizationId, clinicDocumentId: clinicDocumentId ?? '' },
    { skip: !clinicDocumentId || effectiveStatus !== 'processed' },
  );

  if (!document) {
    return <>{dropZone}</>;
  }

  if (isDetailLoading && !effectiveDocument) {
    return (
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 4 }}>
        <CircularProgress size={22} />
        <Typography>Loading document details...</Typography>
      </Stack>
    );
  }

  if (!effectiveDocument) return null;

  const statusChip = getStatusChipProps(effectiveStatus);

  return (
    <Stack spacing={2.5}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            {effectiveDocument.title || effectiveDocument.filename}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {effectiveDocument.filename}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Chip color={statusChip.color} label={statusChip.label} />
          <Button
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => setFileViewerOpen(true)}
          >
            View original
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {effectiveDocument.doc_kind && effectiveDocument.doc_kind !== 'other' && (
          <Chip label={effectiveDocument.doc_kind.replace(/_/g, ' ')} variant="outlined" />
        )}
        <Chip label={effectiveDocument.scope_type} variant="outlined" />
        <Chip label={formatBytes(effectiveDocument.size_bytes)} variant="outlined" />
        {effectiveDocument.page_count !== null && (
          <Chip label={`${effectiveDocument.page_count} page${effectiveDocument.page_count === 1 ? '' : 's'}`} variant="outlined" />
        )}
        <Chip label={`Updated ${formatDateTime(effectiveDocument.updated_at)}`} variant="outlined" />
      </Stack>

      {effectiveDocument.description && (
        <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
          {effectiveDocument.description}
        </Typography>
      )}

      {effectiveDocument.tags.length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {effectiveDocument.tags.map(tag => (
            <Chip key={tag} size="small" label={tag} />
          ))}
        </Stack>
      )}

      <Divider />

      {detailData?.outline_md && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Outline preview
          </Typography>
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'grey.50' }}>
            <DocumentMarkdownRenderer markdown={detailData.outline_md} />
          </Paper>
        </Box>
      )}

      {effectiveStatus === 'processed' && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
            Sections
          </Typography>
          {isSectionsLoading ? (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">Loading sections...</Typography>
            </Stack>
          ) : sectionsData?.sections.length ? (
            <Stack spacing={1.5}>
              {sectionsData.sections.map(section => (
                <ClinicDocumentSectionTreeItem key={section.clinic_document_section_id} section={section} />
              ))}
            </Stack>
          ) : (
            <Alert severity="info">No section tree for this document.</Alert>
          )}
        </Box>
      )}

      <AdminFileViewer
        open={fileViewerOpen}
        onClose={() => setFileViewerOpen(false)}
        organizationId={organizationId}
        clinicDocumentId={clinicDocumentId}
        filename={effectiveDocument.filename}
        title={effectiveDocument.title}
        contentType={effectiveDocument.content_type}
      />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Embeddable admin panel (takes organizationId as prop)
// ---------------------------------------------------------------------------
export function AdminClinicDocumentsEmbedded({ organizationId }: { organizationId: string }) {
  const { enqueueSnackbar } = useSnackbar();
  const [searchQuery, setSearchQuery] = useState('');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [docKindFilter, setDocKindFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [offset, setOffset] = useState(0);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  const [uploadAdminClinicDocuments, { isLoading: isUploading }] =
    useUploadAdminClinicDocumentsMutation();

  const {
    data: listData,
    isLoading,
    isFetching,
    error: listError,
    refetch,
  } = useListAdminClinicDocumentsQuery(
    {
      organizationId,
      scopeType: scopeFilter === 'all' ? undefined : scopeFilter,
      docKind: docKindFilter === 'all' ? undefined : docKindFilter,
      processingStatus: statusFilter === 'all' ? undefined : statusFilter,
      search: searchQuery || undefined,
      limit: PAGE_SIZE,
      offset,
    },
    { skip: !organizationId },
  );

  const documents = listData?.documents ?? EMPTY_DOCUMENTS;
  const total = listData?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const hasNextPage = offset + PAGE_SIZE < total;

  const selectedDocument = documents.find(d => d.clinic_document_id === selectedDocumentId) ?? null;

  // Auto-select first document
  useEffect(() => {
    if (!documents.length) {
      setSelectedDocumentId(null);
      return;
    }
    if (!selectedDocumentId || !documents.some(d => d.clinic_document_id === selectedDocumentId)) {
      setSelectedDocumentId(documents[0].clinic_document_id);
    }
  }, [documents, selectedDocumentId]);

  const resetPagination = useCallback(() => setOffset(0), []);

  const handleUpload = useCallback(
    async (files: File[]) => {
      if (!organizationId || !files.length) return;

      const formData = new FormData();
      files.forEach(file => formData.append('files[]', file));
      formData.append('scope_type', 'organization');

      try {
        const response = await uploadAdminClinicDocuments({ organizationId, body: formData }).unwrap();
        setScopeFilter('all');
        setDocKindFilter('all');
        setStatusFilter('all');
        setSearchQuery('');
        setOffset(0);
        const firstNewId = response.documents[0]?.clinic_document_id ?? null;
        if (firstNewId) setSelectedDocumentId(firstNewId);
        await refetch();

        enqueueSnackbar(
          response.documents.length === 1
            ? 'Document uploaded — metadata will appear shortly.'
            : `${response.documents.length} documents uploaded — metadata will appear shortly.`,
          { variant: 'success' },
        );
      } catch (err: unknown) {
        const message =
          err && typeof err === 'object' && 'data' in err &&
          err.data && typeof err.data === 'object' && 'message' in err.data &&
          typeof err.data.message === 'string'
            ? err.data.message
            : 'Failed to upload clinic documents.';
        enqueueSnackbar(message, { variant: 'error' });
      }
    },
    [enqueueSnackbar, organizationId, refetch, uploadAdminClinicDocuments],
  );

  const handleUploadInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const pdfs = Array.from(e.target.files ?? []).filter(
        file => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'),
      );
      if (pdfs.length) void handleUpload(pdfs);
      if (uploadInputRef.current) uploadInputRef.current.value = '';
    },
    [handleUpload],
  );

  const dropZone = (
    <ClinicDocumentDropZone onFilesDropped={handleUpload} isUploading={isUploading} />
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Sidebar */}
          <Box
            component="aside"
            sx={{
              flexShrink: 0,
              width: CLINIC_DOCS_LAYOUT.SIDEBAR_WIDTH,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              borderRight: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, pt: 1.5, pb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Documents</Typography>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Upload documents">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => uploadInputRef.current?.click()}
                      disabled={isUploading}
                      aria-label="Upload documents"
                    >
                      {isUploading ? (
                        <CircularProgress size={18} />
                      ) : (
                        <CloudUploadOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Refresh list">
                  <IconButton size="small" onClick={() => refetch()} aria-label="Refresh document list">
                    <RefreshOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <input
                ref={uploadInputRef}
                hidden
                multiple
                accept="application/pdf,.pdf"
                type="file"
                onChange={handleUploadInputChange}
              />
            </Stack>

            <Divider />

            {/* Search */}
            <Box sx={{ px: 2, py: 1, flexShrink: 0 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search documents..."
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); resetPagination(); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlinedIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Divider />

            {/* Filters */}
            <Box sx={{ px: 2, py: 1, flexShrink: 0 }}>
              <Stack spacing={1}>
                <Stack direction="row" spacing={0.75}>
                  <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                    <InputLabel id="admin-scope-label">Scope</InputLabel>
                    <Select labelId="admin-scope-label" label="Scope" value={scopeFilter} onChange={e => { setScopeFilter(e.target.value); resetPagination(); }}>
                      {SCOPE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                    <InputLabel id="admin-kind-label">Kind</InputLabel>
                    <Select labelId="admin-kind-label" label="Kind" value={docKindFilter} onChange={e => { setDocKindFilter(e.target.value); resetPagination(); }}>
                      {DOC_KIND_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                    <InputLabel id="admin-status-label">Status</InputLabel>
                    <Select labelId="admin-status-label" label="Status" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); resetPagination(); }}>
                      {STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Stack>
                {(searchQuery || scopeFilter !== 'all' || docKindFilter !== 'all' || statusFilter !== 'all') && (
                  <Button
                    size="small"
                    onClick={() => { setSearchQuery(''); setScopeFilter('all'); setDocKindFilter('all'); setStatusFilter('all'); resetPagination(); }}
                    sx={{ alignSelf: 'flex-end' }}
                  >
                    Clear filters
                  </Button>
                )}
              </Stack>
            </Box>

            <Divider />

            {/* Document list */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1 }}>
              {isLoading ? (
                <Stack spacing={1}>
                  {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="rounded" height={56} />)}
                </Stack>
              ) : listError ? (
                <Alert severity="error" sx={{ mt: 1 }}>Failed to load documents.</Alert>
              ) : documents.length === 0 ? (
                <Alert severity="info" sx={{ mt: 1 }}>No documents match the current filters.</Alert>
              ) : (
                <Stack spacing={0.75}>
                  {documents.map(doc => (
                    <Box
                      key={doc.clinic_document_id}
                      onClick={() => setSelectedDocumentId(doc.clinic_document_id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedDocumentId(doc.clinic_document_id); } }}
                      aria-label={`Select ${doc.title || doc.filename}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: selectedDocumentId === doc.clinic_document_id ? 'primary.main' : 'divider',
                        bgcolor: selectedDocumentId === doc.clinic_document_id ? 'action.selected' : 'background.paper',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: selectedDocumentId === doc.clinic_document_id ? 'action.selected' : 'action.hover' },
                      }}
                    >
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: getStatusColor(doc.processing_status), flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: selectedDocumentId === doc.clinic_document_id ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {doc.title || doc.filename}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                          {[doc.doc_kind && doc.doc_kind !== 'other' ? doc.doc_kind.replace(/_/g, ' ') : null, formatDateTime(doc.created_at)].filter(Boolean).join(' · ')}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}

              {/* Pagination */}
              {documents.length > 0 && (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mt: 1.5, pt: 1, borderTop: 1, borderColor: 'divider' }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Page {currentPage} of {totalPages}
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    <Button size="small" variant="text" onClick={() => setOffset(o => Math.max(0, o - PAGE_SIZE))} disabled={offset === 0}>
                      Prev
                    </Button>
                    <Button size="small" variant="text" onClick={() => setOffset(o => o + PAGE_SIZE)} disabled={!hasNextPage}>
                      Next
                    </Button>
                  </Stack>
                </Stack>
              )}
            </Box>
          </Box>

          {/* Detail panel */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            <AdminDetailPanel
              document={selectedDocument}
              organizationId={organizationId}
              dropZone={dropZone}
            />
          </Box>
        </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Routed page wrapper (reads organizationId from URL params)
// ---------------------------------------------------------------------------
export default function AdminClinicDocumentsPage() {
  const { organizationId = '' } = useParams<{ organizationId: string }>();
  return <AdminClinicDocumentsEmbedded organizationId={organizationId} />;
}
